import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";

import { Section } from "@/components/sections/Section";
import type { Section as SectionType } from "@/types/global";

type GallerySection = Extract<SectionType, { type: "gallery" }>;
type GalleryImage = GallerySection["data"]["images"][number];

function GalleryLightbox({
  images,
  index,
  onClose,
  onNavigate,
}: {
  images: GalleryImage[];
  index: number;
  onClose: () => void;
  onNavigate: (index: number) => void;
}) {
  const image = images[index];

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
      if (event.key === "ArrowLeft") {
        onNavigate((index - 1 + images.length) % images.length);
      }
      if (event.key === "ArrowRight") {
        onNavigate((index + 1) % images.length);
      }
    };

    document.body.classList.add("overflow-hidden");
    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.classList.remove("overflow-hidden");
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [images.length, index, onClose, onNavigate]);

  if (!image) {
    return null;
  }

  const canNavigate = images.length > 1;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 p-4 md:p-8"
      role="dialog"
      aria-modal="true"
      aria-label="Galerie photo"
    >
      <button
        type="button"
        className="absolute right-4 top-4 z-10 inline-flex h-11 w-11 items-center justify-center rounded-sm border border-white/20 bg-white/10 text-white transition-smooth hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        onClick={onClose}
        aria-label="Fermer la galerie"
      >
        <X className="h-6 w-6" aria-hidden />
      </button>

      {canNavigate && (
        <button
          type="button"
          className="absolute left-4 top-1/2 z-10 inline-flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-sm border border-white/20 bg-white/10 text-white transition-smooth hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary md:left-6"
          onClick={() => onNavigate((index - 1 + images.length) % images.length)}
          aria-label="Image précédente"
        >
          <ChevronLeft className="h-7 w-7" aria-hidden />
        </button>
      )}

      <figure className="flex h-full w-full max-w-6xl flex-col items-center justify-center gap-4">
        <img
          src={image.url}
          alt={image.alt}
          width={image.width || undefined}
          height={image.height || undefined}
          className="max-h-[82vh] max-w-full object-contain"
          style={{ objectPosition: image.focus_point }}
        />
        <figcaption className="min-h-5 text-sm text-white/70">
          {canNavigate ? `${index + 1} / ${images.length}` : ""}
        </figcaption>
      </figure>

      {canNavigate && (
        <button
          type="button"
          className="absolute right-4 top-1/2 z-10 inline-flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-sm border border-white/20 bg-white/10 text-white transition-smooth hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary md:right-6"
          onClick={() => onNavigate((index + 1) % images.length)}
          aria-label="Image suivante"
        >
          <ChevronRight className="h-7 w-7" aria-hidden />
        </button>
      )}
    </div>
  );
}

export function Gallery({ settings, data }: GallerySection) {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  return (
    <Section settings={settings}>
      {(data.title || data.content) && (
        <div className="mb-10 max-w-3xl">
          {data.title && (
            <>
              <h2 className="heading-jumbo-small">{data.title}</h2>
              <span className="separateur" />
            </>
          )}
          {data.content && (
            <div
              className="mt-6 text-base md:text-lg leading-relaxed text-foreground/90"
              dangerouslySetInnerHTML={{ __html: data.content }}
            />
          )}
        </div>
      )}
      <div className="grid grid-cols-2 gap-3 md:gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {data.images.map((img, i) => (
          <button
            key={`${img.id}-${i}`}
            type="button"
            className="group block aspect-square overflow-hidden rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"
            onClick={() => setLightboxIndex(i)}
            aria-label={`Ouvrir l'image ${i + 1}`}
          >
            <img
              src={img.url}
              alt={img.alt}
              width={img.width || undefined}
              height={img.height || undefined}
              loading="lazy"
              className="h-full w-full object-cover transition-smooth hover:scale-105"
              style={{ objectPosition: img.focus_point }}
            />
          </button>
        ))}
      </div>
      {data.cta && (
        <a
          href={data.cta.url}
          target={data.cta.target || undefined}
          rel={data.cta.target === "_blank" ? "noopener noreferrer" : undefined}
          className="mt-10 inline-flex items-center justify-center rounded-sm bg-gradient-fire px-7 py-4 text-sm font-bold uppercase text-primary-foreground shadow-fire transition-smooth hover:opacity-95"
        >
          {data.cta.title || "Contactez-nous"}
        </a>
      )}
      {lightboxIndex !== null && (
        <GalleryLightbox
          images={data.images}
          index={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onNavigate={setLightboxIndex}
        />
      )}
    </Section>
  );
}
